import { useMemo } from "react";
import { PERMISSIONS } from "./constants";
import { sub_client_rate_management } from "./SubClientMenuItems";
import { transfer_station_sub_client_rate_management } from "./TransferStationSubclientMenuItems";

/**
 * Custom hook to handle rate maintenance menu filtering and visibility logic
 * 
 * @param subClientPermissions - Array of permission strings for the sub-client
 * @param isTransferStationBusinessTypeEnabled - Whether transfer station mode is enabled
 * @returns Object containing filtered menu items and visibility flag
 */
export const useRateMaintenanceMenu = (
    subClientPermissions: string[],
    isTransferStationBusinessTypeEnabled: boolean
) => {
    const filteredRateManagement = useMemo(() => {
        const mainPermission = isTransferStationBusinessTypeEnabled
            ? PERMISSIONS.TRANSFER_STATION_RATE_MAINTENANCE
            : PERMISSIONS.REGULAR_RATE_MAINTENANCE;

        const baseMenu = isTransferStationBusinessTypeEnabled
            ? transfer_station_sub_client_rate_management
            : sub_client_rate_management;

        if (!subClientPermissions.includes(mainPermission)) {
            // When the main permission itself is missing → restrict more
            return baseMenu.filter(
                (item) =>
                    item.permission !== PERMISSIONS.CUSTOM_RATE_SEARCH &&
                    item.permission !== PERMISSIONS.MANAGE_MINIMUM_WEIGHT_AND_RATE &&
                    item.permission !== PERMISSIONS.MANAGE_RATE_MAINTENANCE &&
                    item.permission !== PERMISSIONS.MANAGE_JURISDICTION_DETAILS &&
                    item.permission !== PERMISSIONS.MANAGE_SPECIAL_SERVICE_RATE
            );
        } else {
            // Main permission exists, but we may still check MANAGE_RATE_MAINTENANCE
            if (!subClientPermissions.includes(PERMISSIONS.MANAGE_RATE_MAINTENANCE)) {
                return baseMenu.filter((item) => item.permission !== mainPermission);
            } else {
                return baseMenu;
            }
        }
    }, [subClientPermissions, isTransferStationBusinessTypeEnabled]);

    const isVisible = useMemo(() => {
        const mainPermission = isTransferStationBusinessTypeEnabled
            ? PERMISSIONS.TRANSFER_STATION_RATE_MAINTENANCE
            : PERMISSIONS.REGULAR_RATE_MAINTENANCE;

        const hasManageRateMaintenance = subClientPermissions.includes(
            PERMISSIONS.MANAGE_RATE_MAINTENANCE
        );
        const hasMainRateMaintenance = subClientPermissions.includes(mainPermission);
        const hasCustomRateOrMinWeight = [
            PERMISSIONS.CUSTOM_RATE_SEARCH,
            PERMISSIONS.MANAGE_MINIMUM_WEIGHT_AND_RATE,
        ].some((p) => subClientPermissions.includes(p));
        const hasManageJurisdiction = subClientPermissions.includes(
            PERMISSIONS.MANAGE_JURISDICTION_DETAILS
        );
        const hasManageSpecialService = subClientPermissions.includes(
            PERMISSIONS.MANAGE_SPECIAL_SERVICE_RATE
        );

        if (
            !hasManageRateMaintenance &&
            !hasMainRateMaintenance &&
            !hasCustomRateOrMinWeight &&
            !hasManageJurisdiction &&
            !hasManageSpecialService
        ) {
            return false;
        }

        return true;
    }, [subClientPermissions, isTransferStationBusinessTypeEnabled]);

    return {
        filteredRateManagement,
        isVisible,
    };
};
